'use client';

import { BIBLE_BOOKS, THEME_COLORS } from '@/lib/constants';

interface SearchFiltersProps {
  testament: string;
  onTestamentChange: (t: string) => void;
  selectedBooks: string[];
  onBooksChange: (books: string[]) => void;
  selectedThemes: string[];
  onThemesChange: (themes: string[]) => void;
}

export default function SearchFilters({
  testament,
  onTestamentChange,
  selectedBooks,
  onBooksChange,
  selectedThemes,
  onThemesChange,
}: SearchFiltersProps) {
  const testamentOptions = [
    { value: '', label: 'All' },
    { value: 'old', label: 'Old Testament' },
    { value: 'new', label: 'New Testament' },
  ];

  const filteredBooks = testament
    ? BIBLE_BOOKS.filter((b) => b.testament === testament)
    : BIBLE_BOOKS;

  const themes = Object.keys(THEME_COLORS);

  return (
    <div className="space-y-6">
      {/* Testament toggle */}
      <div>
        <h3
          className="text-xs font-semibold uppercase tracking-wider mb-3"
          style={{ color: 'var(--text-secondary)' }}
        >
          Testament
        </h3>
        <div className="flex flex-col gap-1">
          {testamentOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onTestamentChange(opt.value)}
              className="text-left text-sm px-3 py-1.5 rounded-lg transition-colors"
              style={{
                backgroundColor:
                  testament === opt.value
                    ? 'rgba(200, 164, 94, 0.12)'
                    : 'transparent',
                color:
                  testament === opt.value
                    ? 'var(--accent-gold)'
                    : 'var(--text-secondary)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Book select */}
      <div>
        <h3
          className="text-xs font-semibold uppercase tracking-wider mb-3"
          style={{ color: 'var(--text-secondary)' }}
        >
          Books
        </h3>
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {filteredBooks.map((book) => {
            const selected = selectedBooks.includes(book.name);
            return (
              <button
                key={book.name}
                onClick={() =>
                  onBooksChange(
                    selected
                      ? selectedBooks.filter((b) => b !== book.name)
                      : [...selectedBooks, book.name]
                  )
                }
                className="flex items-center gap-2 w-full text-left text-xs px-2 py-1 rounded transition-colors"
                style={{
                  color: selected
                    ? 'var(--accent-gold)'
                    : 'var(--text-secondary)',
                  backgroundColor: selected
                    ? 'rgba(200, 164, 94, 0.08)'
                    : 'transparent',
                }}
              >
                <span
                  className="w-3 h-3 rounded-sm border shrink-0 flex items-center justify-center"
                  style={{
                    borderColor: selected
                      ? 'var(--accent-gold)'
                      : 'rgba(200, 164, 94, 0.2)',
                    backgroundColor: selected
                      ? 'var(--accent-gold)'
                      : 'transparent',
                  }}
                >
                  {selected && (
                    <span className="text-[8px]" style={{ color: 'var(--bg-primary)' }}>
                      ✓
                    </span>
                  )}
                </span>
                {book.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Theme checkboxes */}
      <div>
        <h3
          className="text-xs font-semibold uppercase tracking-wider mb-3"
          style={{ color: 'var(--text-secondary)' }}
        >
          Themes
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {themes.map((theme) => {
            const selected = selectedThemes.includes(theme);
            const color = THEME_COLORS[theme];
            return (
              <button
                key={theme}
                onClick={() =>
                  onThemesChange(
                    selected
                      ? selectedThemes.filter((t) => t !== theme)
                      : [...selectedThemes, theme]
                  )
                }
                className="text-xs px-2.5 py-1 rounded-full capitalize transition-all"
                style={{
                  backgroundColor: selected
                    ? `${color}25`
                    : 'rgba(255,255,255,0.05)',
                  color: selected ? color : 'var(--text-secondary)',
                  border: `1px solid ${
                    selected ? `${color}50` : 'transparent'
                  }`,
                }}
              >
                {theme}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
